import React, { useState, useEffect, useCallback } from "react";
import { Container, Box, Tabs, Tab, Paper, CircularProgress, Alert } from "@mui/material";
import { Globe, Settings, Palette, ShoppingBag, MessageCircle, CreditCard, Share2, Eye, ShieldCheck } from "lucide-react";
import TitleBox from "../Shared/TitleBox";
import { getWebsiteSettings, updateWebsiteSettings, toggleWebsitePublished } from "../../api/index";
import GeneralTab from "./tabs/GeneralTab";
import AppearanceTab from "./tabs/AppearanceTab";
import CatalogTab from "./tabs/CatalogTab";
import CartWhatsAppTab from "./tabs/CartWhatsAppTab";
import PaymentMethodsTab from "./tabs/PaymentMethodsTab";
import SocialLinksTab from "./tabs/SocialLinksTab";
import PrivacyPolicyTab from "./tabs/PrivacyPolicyTab";
import DataCollectionTab from "./tabs/DataCollectionTab";
import PreviewTab from "./tabs/PreviewTab";
import { useTranslation } from "react-i18next";
const TABS = [{
  label: "General",
  icon: <Settings size={16} />
}, {
  label: "Appearance",
  icon: <Palette size={16} />
}, {
  label: "Products",
  icon: <ShoppingBag size={16} />
}, {
  label: "Cart & WhatsApp",
  icon: <MessageCircle size={16} />
}, {
  label: "Payments",
  icon: <CreditCard size={16} />
}, {
  label: "Social Links",
  icon: <Share2 size={16} />
}, {
  label: "Privacy Policy",
  icon: <ShieldCheck size={16} />
}, {
  label: "Data Collection",
  icon: <ShieldCheck size={16} />
}, {
  label: "Preview",
  icon: <Eye size={16} />
}];

const getWebsiteTabLabelKey = tabConfig => tabConfig.label;

const WebsiteManager = () => {
  const {
    t
  } = useTranslation("website");
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWebsiteSettings();
      setSettings(res.data?.data ?? res.data);
    } catch (e) {
      setError(e.response?.data?.message || t("Failed to load settings"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => {
    load();
  }, [load]);
  const save = async patch => {
    setSaving(true);
    try {
      const res = await updateWebsiteSettings({
        ...settings,
        ...patch
      });
      setSettings(res.data?.data ?? res.data);
    } catch (e) {
      alert(e.response?.data?.message || t("Save failed"));
    } finally {
      setSaving(false);
    }
  };
  const handleTogglePublish = async () => {
    setSaving(true);
    try {
      const res = await toggleWebsitePublished();
      setSettings(prev => ({
        ...prev,
        isPublished: res.data?.data?.isPublished
      }));
    } catch (e) {
      alert(e.response?.data?.message || t("Failed to toggle"));
    } finally {
      setSaving(false);
    }
  };
  const sharedProps = {
    settings,
    save,
    saving,
    reload: load
  };
  return <Container maxWidth={false} className="AppContainer-root">
      <TitleBox title={t("My Website")} subTitle={t("Manage your public e-commerce website")} allowPrint={false} />
      

      {loading && <Box sx={{
      textAlign: "center",
      py: 8
    }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{
      mb: 2
    }}>{error}</Alert>}

      {!loading && settings && <Paper elevation={1} sx={{
      borderRadius: 2,
      overflow: "hidden"
    }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "grey.50"
      }}>
          
            {TABS.map((tabConfig, i) => <Tab key={i} label={<Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75
        }}>
                    {tabConfig.icon}{t(getWebsiteTabLabelKey(tabConfig))}
                  </Box>} />)}
          </Tabs>

          <Box sx={{
        p: 3
      }}>
            {tab === 0 && <GeneralTab {...sharedProps} onTogglePublish={handleTogglePublish} />}
            {tab === 1 && <AppearanceTab {...sharedProps} />}
            {tab === 2 && <CatalogTab {...sharedProps} />}
            {tab === 3 && <CartWhatsAppTab {...sharedProps} />}
            {tab === 4 && <PaymentMethodsTab />}
            {tab === 5 && <SocialLinksTab {...sharedProps} />}
            {tab === 6 && <PrivacyPolicyTab {...sharedProps} />}
            {tab === 7 && <DataCollectionTab {...sharedProps} />}
            {tab === 8 && <PreviewTab settings={settings} onTogglePublish={handleTogglePublish} saving={saving} />}
          </Box>
        </Paper>}
    </Container>;
};
export default WebsiteManager;
